import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppContext } from "../../../context/AppContext";
import { CourseClassDetailTrans } from "../../../utils/translations";
import { getCourseClass, purchaseCourse } from "../../../services/courseService";
import ApproxPrice from "../../ui/ApproxPrice";

const isValidUrl = (value) => {
  if (!value || typeof value !== "string") return false;
  try {
    new URL(value);
    return true;
  } catch (error) {
    return false;
  }
};

const toYoutubeEmbedUrl = (url) => {
  if (!isValidUrl(url)) return "";

  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.hostname.includes("youtu.be")) {
      const id = parsedUrl.pathname.replace("/", "");
      return id ? `https://www.youtube.com/embed/${id}` : "";
    }
    if (parsedUrl.hostname.includes("youtube.com")) {
      const id = parsedUrl.searchParams.get("v");
      return id ? `https://www.youtube.com/embed/${id}` : "";
    }
  } catch (error) {
    return "";
  }

  return "";
};

export default function CourseClassDetail() {
  const { courseSlug, classSlug } = useParams();
  const { language, token } = useAppContext();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const t = CourseClassDetailTrans[language] || CourseClassDetailTrans.english;

  const [payload, setPayload] = useState(null);
  const [locked, setLocked] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [purchasingProvider, setPurchasingProvider] = useState(null);
  const [purchaseError, setPurchaseError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadClass = async () => {
      setIsLoading(true);
      setError("");
      setLocked(null);

      try {
        const result = await getCourseClass(courseSlug, classSlug, token);

        if (result.locked) {
          if (isMounted) {
            setLocked({ nisPrice: result.nisPrice, rubPrice: result.rubPrice, eurDisplayPrice: result.eurDisplayPrice });
          }
          return;
        }

        if (isMounted) {
          setPayload(result.data);
        }
      } catch (err) {
        if (isMounted) {
          console.error(err);
          setError(t.error);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadClass();
    return () => {
      isMounted = false;
    };
  }, [courseSlug, classSlug, t.error, token]);

  const handlePurchase = async (provider) => {
    if (!isAuthenticated) {
      loginWithRedirect();
      return;
    }
    setPurchasingProvider(provider);
    setPurchaseError("");
    try {
      const { paymentUrl } = await purchaseCourse(courseSlug, provider, token);
      window.location.href = paymentUrl;
    } catch (err) {
      console.error(err);
      setPurchaseError(t.purchaseError);
      setPurchasingProvider(null);
    }
  };

  const youtubeEmbedUrl = useMemo(
    () => toYoutubeEmbedUrl(payload?.class?.youtubeUrl),
    [payload?.class?.youtubeUrl]
  );
  const pdfUrls = Array.isArray(payload?.class?.pdfUrls)
    ? payload.class.pdfUrls.filter(isValidUrl)
    : [];
  const imageUrls = Array.isArray(payload?.class?.imageUrls)
    ? payload.class.imageUrls.filter(isValidUrl)
    : [];

  return (
    <main className="min-h-screen mt-10 px-5 py-8 lg:px-16">
      <div className="mx-auto w-full max-w-5xl">
        {isLoading && <p className="text-center">{t.loading}</p>}
        {!isLoading && error && <p className="text-center text-red-600">{error}</p>}

        {!isLoading && !error && locked && (
          <div className="mx-auto max-w-md rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm">
            <a
              href={`/courses/${courseSlug}`}
              className="inline-flex text-sm text-brown underline"
            >
              {t.backToCourse}
            </a>
            <h1 className="mt-3 text-2xl font-semibold">{t.locked}</h1>
            <p className="mt-2 text-gray-700">{t.lockedHint}</p>
            {(locked.nisPrice > 0 || locked.rubPrice > 0) && locked.eurDisplayPrice > 0 && (
              <p className="mt-3 text-xl font-semibold">
                <ApproxPrice
                  eur={locked.eurDisplayPrice}
                  charges={[{ amount: locked.nisPrice, currency: 'ILS' }, { amount: locked.rubPrice, currency: 'RUB' }]}
                  subClassName="text-gray-500"
                />
              </p>
            )}
            {(locked.nisPrice > 0 || locked.rubPrice > 0) && !(locked.eurDisplayPrice > 0) && (
              <p className="mt-3 text-xl font-semibold">
                {locked.nisPrice > 0 && `${locked.nisPrice} ₪`}
                {locked.nisPrice > 0 && locked.rubPrice > 0 ? " / " : ""}
                {locked.rubPrice > 0 && `${locked.rubPrice} ₽`}
              </p>
            )}
            {purchaseError && <p className="mt-3 text-sm text-red-600">{purchaseError}</p>}
            <div className="mt-5 flex flex-col gap-3">
              {!isAuthenticated ? (
                <button
                  onClick={() => loginWithRedirect()}
                  className="rounded-md bg-brown px-4 py-2 text-sm font-medium text-white"
                >
                  {t.loginToBuy}
                </button>
              ) : (
                <>
                  {locked.nisPrice > 0 && (
                    <button
                      disabled={!!purchasingProvider}
                      onClick={() => handlePurchase("allpay")}
                      className="rounded-md bg-brown px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                    >
                      {purchasingProvider === "allpay" ? t.redirecting : t.buyWithAllpay}
                    </button>
                  )}
                  {locked.rubPrice > 0 && (
                    <button
                      disabled={!!purchasingProvider}
                      onClick={() => handlePurchase("yookassa")}
                      className="rounded-md border border-brown px-4 py-2 text-sm font-medium text-brown disabled:opacity-60"
                    >
                      {purchasingProvider === "yookassa" ? t.redirecting : t.buyWithYookassa}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {!isLoading && !error && payload?.class && (
          <>
            <a
              href={`/courses/${payload.course?.slug || courseSlug}`}
              className="inline-flex text-sm text-brown underline"
            >
              {t.backToCourse}
            </a>

            <h1 className="mt-3 text-3xl font-semibold">{payload.class.title}</h1>
            {payload.class.summary && <p className="mt-2 text-gray-700">{payload.class.summary}</p>}

            {youtubeEmbedUrl && (
              <section className="mt-8">
                <div className="aspect-video w-full overflow-hidden rounded-lg">
                  <iframe
                    title={payload.class.title}
                    src={youtubeEmbedUrl}
                    className="h-full w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
                <a
                  href={payload.class.youtubeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex text-sm text-brown underline"
                >
                  {t.watchOnYoutube}
                </a>
              </section>
            )}

            {payload.class.contentText && (
              <section className="mt-8">
                <h2 className="text-xl font-semibold">{t.textContent}</h2>
                <p className="mt-2 whitespace-pre-wrap text-gray-700">{payload.class.contentText}</p>
              </section>
            )}

            {pdfUrls.length > 0 && (
              <section className="mt-8">
                <h2 className="text-xl font-semibold">{t.materials}</h2>
                <div className="mt-3 flex flex-wrap gap-3">
                  {pdfUrls.map((url, index) => (
                    <a
                      key={`${url}-${index}`}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-100"
                    >
                      {t.openPdf} {index + 1}
                    </a>
                  ))}
                </div>
              </section>
            )}

            {imageUrls.length > 0 && (
              <section className="mt-8">
                <h2 className="text-xl font-semibold">{t.images}</h2>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  {imageUrls.map((url, index) => (
                    <img
                      key={`${url}-${index}`}
                      src={url}
                      alt={`${payload.class.title}-${index + 1}`}
                      className="w-full rounded-lg border border-gray-200 object-cover"
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </main>
  );
}
